<!--
  NOTE: This component uses a placeholder for the @/components/Partner/PartnerEmploymentsTable component.
  The real one will be built with @/components/GraphqlDataTable/GraphqlDataTable.vue

  This is setup with some fake static data that was used to build the placeholder.
  Of course disregard everything about this data since actual data will be fetched from the Public API.
-->
<template>
  <PageHeader>
    <template #header-title>Employments</template>
    <template #header-description>Choose workers to submit to this job</template>
  </PageHeader>

  <WdsTabs class="tw-mb-4">
    <WdsTab :tab-key="'new'" title="New">
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('New') }}
          <WdsLabel v-if="newEmployments.length > 0" xs>{{ newEmployments.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerEmploymentsTable :employments="newEmployments" />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'updated'" title="Updated">
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('Updated') }}
          <WdsLabel v-if="updatedEmployments.length > 0" xs>{{ updatedEmployments.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerEmploymentsTable :employments="updatedEmployments" />
      </div>
    </WdsTab>
    <WdsTab :tab-key="'onboarded'" title="Onboarded">
      <template #title>
        <WdsStack align-center gap-sm>
          {{ $t('Onboarded') }}
          <WdsLabel v-if="onboardedEmployments.length > 0" xs>{{ onboardedEmployments.length }}</WdsLabel>
        </WdsStack>
      </template>
      <div class="tw-mt-2">
        <PartnerEmploymentsTable :employments="onboardedEmployments" />
      </div>
    </WdsTab>
  </WdsTabs>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { WdsTabs, WdsTab, WdsStack, WdsLabel } from '@worksome/wds-components'
import PageHeader from '@/components/PageHeader.vue'
import PartnerEmploymentsTable from './PartnerEmploymentsTable.vue'
import { $t } from '@/composables/translation'

// Fake data for employments tables
const newEmployments = ref([
  {
    id: '1',
    worker: {
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      location: 'United Kingdom'
    },
    job: {
      title: 'Senior Frontend Developer',
      id: 'JOB-12345'
    },
    contract: 'Contractor',
    startDate: '2023-06-01',
    endDate: '2023-12-31',
    previouslyHired: false,
    assignmentCompany: 'Acme Corp Ltd.',
    hiringManager: 'Emily Johnson'
  },
  {
    id: '2',
    worker: {
      name: 'John Doe',
      email: 'john.doe@example.com',
      location: 'Denmark'
    },
    job: {
      title: 'Backend Developer',
      id: 'JOB-12346'
    },
    contract: 'Full-time',
    startDate: '2023-07-15',
    endDate: '2024-01-15',
    previouslyHired: true,
    assignmentCompany: 'Tech Solutions ApS',
    hiringManager: 'Sophia Brown'
  },
  {
    id: '3',
    worker: {
      name: 'Alex Johnson',
      email: 'alex.johnson@example.com',
      location: 'Germany'
    },
    job: {
      title: 'UX Designer',
      id: 'JOB-12347'
    },
    contract: 'Contractor',
    startDate: '2023-08-01',
    endDate: '2023-11-30',
    previouslyHired: false,
    assignmentCompany: 'Design Partners GmbH',
    hiringManager: 'Olivia Wilson'
  }
])

const updatedEmployments = ref([
  {
    id: '4',
    worker: {
      name: 'Sarah Williams',
      email: 'sarah.williams@example.com',
      location: 'United States'
    },
    job: {
      title: 'Project Manager',
      id: 'JOB-12348'
    },
    contract: 'Full-time',
    startDate: '2023-05-15',
    endDate: '2024-05-14',
    previouslyHired: true,
    assignmentCompany: 'Global Projects Inc.',
    hiringManager: 'Isabella Martinez'
  },
  {
    id: '5',
    worker: {
      name: 'Michael Brown',
      email: 'michael.brown@example.com',
      location: 'Sweden'
    },
    job: {
      title: 'DevOps Engineer',
      id: 'JOB-12349'
    },
    contract: 'Contractor',
    startDate: '2023-09-01',
    endDate: '2024-03-01',
    previouslyHired: false,
    assignmentCompany: 'Nordic Tech AB',
    hiringManager: 'Mia Rodriguez'
  }
])

const onboardedEmployments = ref([
  {
    id: '6',
    worker: {
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      location: 'France'
    },
    job: {
      title: 'Data Scientist',
      id: 'JOB-12350'
    },
    contract: 'Full-time',
    startDate: '2023-04-01',
    endDate: '2024-04-01',
    previouslyHired: true,
    assignmentCompany: 'Data Analytics SARL',
    hiringManager: 'Ava Lee'
  },
  {
    id: '7',
    worker: {
      name: 'Robert Wilson',
      email: 'robert.wilson@example.com',
      location: 'Spain'
    },
    job: {
      title: 'Frontend Developer',
      id: 'JOB-12351'
    },
    contract: 'Contractor',
    startDate: '2023-07-01',
    endDate: '2023-12-31',
    previouslyHired: false,
    assignmentCompany: 'Web Solutions SL',
    hiringManager: 'Charlotte Hall'
  },
  {
    id: '8',
    worker: {
      name: 'Linda Miller',
      email: 'linda.miller@example.com',
      location: 'Netherlands'
    },
    job: {
      title: 'UI Designer',
      id: 'JOB-12352'
    },
    contract: 'Full-time',
    startDate: '2023-06-15',
    endDate: '2024-06-14',
    previouslyHired: true,
    assignmentCompany: 'Creative Design BV',
    hiringManager: 'Charlotte Hall'
  }
])
</script>
